import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ThemeCustomizer } from "@/components/admin/ThemeCustomizer";
import { Users, Building2, Inbox, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const { data: talentCount } = useQuery({
    queryKey: ["talent-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("talent")
        .select("*", { count: "exact", head: true });
      return count || 0;
    },
  });

  const { data: clientsCount } = useQuery({
    queryKey: ["clients-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("clients")
        .select("*", { count: "exact", head: true });
      return count || 0;
    },
  });

  const { data: newRequestsCount } = useQuery({
    queryKey: ["new-requests-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("talent_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "new");
      return count || 0;
    },
  });

  const { data: recentRequests } = useQuery({
    queryKey: ["recent-requests"],
    queryFn: async () => {
      const { data } = await supabase
        .from("talent_requests")
        .select("*, talent:talent_id(name)")
        .order("created_at", { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  const stats = [
    { label: "Total Talent", value: talentCount, icon: Users, href: "/admin/talent" },
    { label: "Active Clients", value: clientsCount, icon: Building2, href: "/admin/clients" },
    { label: "New Requests", value: newRequestsCount, icon: Inbox, href: "/admin/inbox" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome to the First Stage content management system.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Link key={stat.label} to={stat.href}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className="h-5 w-5 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stat.value ?? "..."}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock size={18} />
            Recent Talent Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentRequests && recentRequests.length > 0 ? (
            <div className="space-y-4">
              {recentRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="font-medium">{request.name}</p>
                    {request.company && (
                      <p className="text-sm text-muted-foreground">{request.company}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{request.email}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="text-sm font-medium">
                      {request.request_type === "application"
                        ? "Talent Application"
                        : (request.talent as { name: string } | null)?.name
                        ? `Talent Request`
                        : "General Inquiry"}
                    </p>
                    {(request.talent as { name: string } | null)?.name && (
                      <p className="text-xs text-muted-foreground">
                        {(request.talent as { name: string }).name}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        request.status === "new"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {request.status}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No requests yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
